import { useCallback, useMemo, useRef } from 'react';

import { USER_ID_TYPE } from '../consts';
import { useDeletedUsers } from '../DeletedUsers';
import { useTranslation } from '../i18n';
import { User } from '../types';
import { useUsers } from '../UsersContext';

export type DisplayUserType = {
  displayName?: string;
  userDetails?: User;
};

export type FormattedUserReturnValue = {
  formatDisplayUser(
    userId: string,
    options?: FormattedDisplayUserOptions,
  ): DisplayUserType;
  loading: boolean;
  error: null | unknown;
};

export type FormattedDisplayUserOptions = {
  ignoreDeletedFormat?: boolean;
};

export type FormattedUserParams = {
  searchBy?: string;
};

const DEFAULT_FORMATTED_USER: FormattedUserParams = {
  searchBy: USER_ID_TYPE.AUTODESK_ID,
};

export const useFormattedUser = ({
  searchBy = USER_ID_TYPE.AUTODESK_ID,
}: FormattedUserParams = DEFAULT_FORMATTED_USER): FormattedUserReturnValue => {
  const { t } = useTranslation();

  const { data: users, loading: usersLoading, error: usersError } = useUsers();

  const {
    data: deletedUsers,
    loading: deletedUsersLoading,
    error: deletedUsersError,
  } = useDeletedUsers();

  const usersById: Map<string, User> = useMemo(() => {
    const allUsers: User[] = users.concat(deletedUsers);
    return new Map<string, User>(
      allUsers.map((user) =>
        searchBy === USER_ID_TYPE.AUTODESK_ID
          ? [user.autodeskId, user]
          : [user.id, user],
      ),
    );
  }, [users, deletedUsers, searchBy]);

  const userNotFoundMap = useRef(new Set<string>());

  const formatDisplayUser = useCallback(
    (
      userId: string,
      { ignoreDeletedFormat = false }: FormattedDisplayUserOptions = {},
    ): DisplayUserType => {
      const currentUser = usersById.get(userId);

      if (
        !userId ||
        (!currentUser &&
          (deletedUsersLoading ||
            usersLoading ||
            usersError ||
            deletedUsersError))
      ) {
        return { displayName: undefined };
      }

      if (currentUser) {
        const { status, name, email } = currentUser;
        const username = name || email || t('NOT_AVAILABLE');
        if (!ignoreDeletedFormat && status === 'deleted') {
          return {
            userDetails: currentUser,
            displayName: t('REMOVED_USER_FORMAT', {
              username,
            }),
          };
        }

        return {
          userDetails: currentUser,
          displayName: username,
        };
      }

      if (!userNotFoundMap.current.has(userId)) {
        const infoMessage = `Tried to format user - ${userId} was searched by ${searchBy} and was not found in users list`;

        console.info(infoMessage);
        userNotFoundMap.current.add(userId);
      }

      return { displayName: undefined };
    },
    [
      usersById,
      deletedUsersLoading,
      usersLoading,
      usersError,
      deletedUsersError,
      searchBy,
      t,
    ],
  );

  return {
    formatDisplayUser,
    loading: usersLoading || deletedUsersLoading,
    error: usersError || deletedUsersError,
  };
};
